import React, { useEffect, useState } from "react";
import { X, Bell } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { enablePush, pushSupported } from "@/lib/push";

const CATS = {
  client: [
    { key: "reports", label: "Visit reports", hint: "When a new inspection report is ready" },
    { key: "messages", label: "Messages", hint: "When your Solvana team replies" },
    { key: "invoices", label: "Invoices", hint: "When a new invoice is issued" },
    { key: "quotes", label: "Quotes", hint: "When a service plan quote is sent" },
    { key: "contracts", label: "Service Agreements", hint: "When an agreement is ready to sign" },
    { key: "trips", label: "Trips", hint: "Updates on your planned arrivals" },
  ],
  staff: [
    { key: "messages", label: "New messages", hint: "When an owner sends a message" },
    { key: "trips", label: "Trips booked", hint: "When an owner plans an arrival" },
    { key: "reports", label: "Reports", hint: "New reports across properties" },
    { key: "invoices", label: "Invoices", hint: "New invoices raised" },
  ],
};

export default function NotificationSettings({ role, onClose }) {
  const cats = CATS[["staff", "admin"].includes(role) ? "staff" : "client"];
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/push/prefs").then((r) => setPrefs(r.data)).catch(() => setPrefs({}));
  }, []);

  const toggle = async (key) => {
    const next = { ...prefs, [key]: !(prefs[key] !== false) };
    setPrefs(next);
    setSaving(true);
    try { await api.patch("/push/prefs", { prefs: next }); }
    catch { toast.error("Couldn't save preferences"); }
    finally { setSaving(false); }
  };

  const enable = async () => {
    try { await enablePush(); toast.success("Notifications enabled on this device"); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="portal-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center", zIndex: 90 }} onClick={onClose}>
      <div data-testid="notif-settings-modal" onClick={(e) => e.stopPropagation()} style={{ background: "var(--paper)", border: "1px solid var(--line)", borderRadius: 14, width: "min(460px,92vw)", maxHeight: "88vh", overflowY: "auto", padding: "26px 26px 22px", boxShadow: "0 24px 60px -18px rgba(0,0,0,.4)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
          <div>
            <div className="lbl" style={{ marginBottom: 4 }}>Notifications</div>
            <div style={{ fontSize: 13, color: "var(--muted)", maxWidth: "40ch" }}>Choose which alerts you receive. Applies to this account across your devices.</div>
          </div>
          <button data-testid="notif-settings-close" onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", cursor: "pointer", color: "var(--muted)" }}><X size={20} /></button>
        </div>

        {pushSupported() && (
          <button data-testid="notif-enable-device" onClick={enable} className="side-link" style={{ margin: "14px 0 18px", padding: "10px 16px", width: "100%", justifyContent: "center", border: "1px solid var(--line)", borderRadius: 10 }}>
            <Bell size={16} /> Enable alerts on this device
          </button>
        )}

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {prefs === null && <div style={{ fontSize: 13, color: "var(--muted)", padding: "8px 0" }}>Loading…</div>}
          {prefs && cats.map((c) => {
            const on = prefs[c.key] !== false;
            return (
              <div key={c.key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 2px", borderBottom: "1px solid var(--line)" }}>
                <div style={{ paddingRight: 14 }}>
                  <div style={{ fontSize: 14, color: "var(--ink)" }}>{c.label}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)" }}>{c.hint}</div>
                </div>
                <button
                  data-testid={`notif-toggle-${c.key}`}
                  role="switch"
                  aria-checked={on}
                  disabled={saving}
                  onClick={() => toggle(c.key)}
                  style={{ flexShrink: 0, width: 44, height: 26, borderRadius: 999, border: "none", cursor: "pointer", position: "relative", background: on ? "var(--honey)" : "var(--line)", transition: "background-color .2s" }}
                >
                  <span style={{ position: "absolute", top: 3, left: on ? 21 : 3, width: 20, height: 20, borderRadius: "50%", background: "#fff", transition: "left .2s", boxShadow: "0 1px 3px rgba(0,0,0,.3)" }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
